import { Component, OnInit, Input, Output, EventEmitter, SimpleChange } from '@angular/core';
import { TableColumn } from '../../../models/tables/TableColumn';
import { Router } from '@angular/router';
import { AlertService } from '../../../services/alert.service';
import { COLUMN_TYPES } from '../../../contants/COLUMN_TYPES';
import { MatDialog } from '@angular/material';
import { ZtsiDeleteConfirmationDialogComponent } from '../ztsi-delete-confirmation-dialog/ztsi-delete-confirmation-dialog.component';

@Component({
	selector: 'ztsi-table',
	templateUrl: './ztsi-table.component.html',
	styleUrls: ['./ztsi-table.component.css']
})
export class ZtsiTableComponent implements OnInit {
	@Input() columns: TableColumn[]; //columns for table
	@Input() data: any[]; //rows or data for table
	@Input() itemFormPath: string = '';
	@Input() itemViewPath: string = '';
	
	@Input() pageSize: number = 2;
	@Input() totalPageNumber: number = 1;
	@Input() pageNumber: number = 1;
	
	@Input() allowEditMode: boolean = false; //allows edit mode for the whole table
	@Input() allowDelete: boolean = false;
	@Input() showActionButtons: boolean = true;
	@Input() showDeleteConfirmation: boolean = false;
	
	@Output() onSave: EventEmitter<number> = new EventEmitter();
	@Output() onCreate: EventEmitter<any> = new EventEmitter();
	@Output() onDelete: EventEmitter<number> = new EventEmitter();
	@Output() onEditBegin: EventEmitter<number> = new EventEmitter();
	@Output() onEditCancel: EventEmitter<number> = new EventEmitter();
	@Output() onFetchNextData: EventEmitter<number> = new EventEmitter();

	selectedRowIndex: number = -1;
	rowEditMode: boolean = false;
	pages: number[];

	constructor(
		private _router: Router,
		private _alertService: AlertService,
		private _dialog: MatDialog
	) { }

	ngOnInit() {
		//ztsi app will always have /:id on its form paths, clean this
		this.itemFormPath = this.itemFormPath.replace('/:id', '');
		this.itemViewPath = this.itemViewPath.replace('/:id', '');
		this.assignPages();
	}

	saveRowData() {
		this.onSave.emit(this.getDataArrayIndex());
		this.selectedRowIndex = -1;
		this.rowEditMode = false;
	}

	onAdd() {
		if (!this.allowEditMode){
			this._router.navigate([this.itemFormPath, 0]);
			return;
		}

		for (let i = this.data.length - 1; i >= 0; i--) {
			if (isNaN(Number(this.data[i].id))) {
				this._alertService.showWarning('A new item exists, please save it first');
				return;
			}
		}

		this.onCreate.emit();
		let lastIndex = this.data.length - 1;
		this.onEdit(null, this.data[lastIndex], lastIndex);
	}

	goToEditFormPage() {
		if (isNaN(Number(this.selectedRowIndex)) || this.selectedRowIndex < 0) {
			this._alertService.showWarning('No row is selected');
		}
		this._router.navigate([this.itemFormPath, this.data[this.selectedRowIndex].id]);
	}

	onEdit(event: Event, rowData: any, index: number) {
		if (!this.allowEditMode) {
			this.selectedRowIndex = index;
			this.goToEditFormPage();
			return;
		}

		if (this.rowEditMode) {
			this.onEditCancel.emit(this.getDataArrayIndex());
		}

		this.rowEditMode = true;
		this.selectedRowIndex = index;
		this.onEditBegin.emit(this.getDataArrayIndex());
	}

	onSelect(event, rowData: any, index: number) {
		if (event.target.tagName != 'TD') {
			event.stopPropagation();
			return;
		}

		if (this.selectedRowIndex != index) {
			if (this.rowEditMode) {
				this.onEditCancel.emit(this.getDataArrayIndex());
			}
			
			this.rowEditMode = false;
			this.selectedRowIndex = index;
		}
	}

	onCancel() {
		this.onEditCancel.emit(this.getDataArrayIndex());
		this.rowEditMode = false;
		this.selectedRowIndex = -1;
	}

	onRemove(index: number) {
		if(this.showDeleteConfirmation){
			this.openDeleteDialogConfirmation(index);
			return;
		}

		// this.data.splice(this.getDataArrayIndex(index), 1);
		this.onDelete.emit(index);		
	}

	openDeleteDialogConfirmation(index: number) {
		this._dialog.closeAll();
		let dialogRef = this._dialog.open(ZtsiDeleteConfirmationDialogComponent, {
		  width: "550px"
		});
	
		dialogRef.afterClosed().subscribe((confirm: boolean) => {
		  if (confirm) {
			this.onDelete.emit(index);
		  }
		});
	  }

	onView(event: Event, rowData: any) {
		let path = this.itemViewPath;
		if(path == null || path == undefined || path.length == 0){
			path = this.itemFormPath;
		}
		this._router.navigate([path, rowData.id]);
	}

	isEditable(rowData: any, column: TableColumn): boolean {
		if (!this.allowEditMode) {
			return false;
		}

		if (column.isEditableOnNew && (rowData.id == undefined || rowData.id == 0)) {
			return true;
		}

		if (column.isEditable && rowData.id != 0) {
			return true;
		}

		return false;
	}

	isRowOnEditMode(index: number): boolean {
		return this.rowEditMode && this.selectedRowIndex == index;
	}

	isObjectOnEditMode(index: number, rowData: any, column: TableColumn){
		if(this.isRowOnEditMode(index)){
			return !this.isEditable(rowData, column);
		}
		return true;
	}

	getDisplayValue(rowData: any, column: TableColumn){
		if(column.dataType == COLUMN_TYPES.container){
			return;
		}

		if(column.dataType == COLUMN_TYPES.date){
			return;
		}

		if(column.dataType == COLUMN_TYPES.bool){
			return;
		}
		
		let value = rowData[column.name]
		if(value == undefined){
			return;
		}

		if(column.dataType == COLUMN_TYPES.select){
			let index = column.selectOptions.findIndex(x => x.key = value);
			return column.selectOptions[index].value;
		}

		return value;
	}

	private getDataArrayIndex(index?: number): number {
		index = index === undefined ? this.selectedRowIndex : index;
		return ((this.pageNumber - 1) * this.pageSize) + index;
	}
	
	ngOnChanges(changes: {[propertyName: string]: SimpleChange}) {
		if(changes.totalPageNumber != undefined){
		  this.totalPageNumber = changes.totalPageNumber.currentValue;
		  this.assignPages();
		}
	}

	// PAGINATION
	goToPage(page: number){
		if(page > this.totalPageNumber || page < 1){
			return;
		}

		this.pageNumber = page;
		if(this.rowEditMode){
			this.rowEditMode = false;
			this.selectedRowIndex = -1;
		}
		
		this.onFetchNextData.emit(page);
	}

	assignPages(){
		let startPage = this.pageNumber > 3 ? this.pageNumber - 2 : 1;
		let endPage = this.pageNumber >= this.totalPageNumber ? this.pageNumber + 2: this.totalPageNumber;
		
		this.pages = [];
		for(startPage; startPage <= endPage; startPage++){
			this.pages.push(startPage);
		}
	}
	// PAGINATION
}



// WEBPACK FOOTER //
// ./src/app/components/shared/ztsi-table/ztsi-table.component.ts